<h4>NuReferral 340B Referral Program Protocol</h4>

@if (!loading && client) {
  <app-client-info-card [client]="client" />

  <app-client-configuration-approval-button [approval]="approval" (approve)="onApprove()" />

  <div class="mt-4 border rounded overflow-hidden">
    <div class="bg-dark text-white p-2 px-3 text-uppercase protocol-section-header">
      Primary Program Configurations
    </div>

    <div class="px-3 py-2 border-bottom protocol-subsection-header text-uppercase">
      Patient Eligibility
    </div>

    <div class="protocol-rows">
      <div class="row g-0 px-3 py-3 protocol-row">
        <div class="col-5 ">Patient Eligibility Window</div>
        <div class="col-7">
          There must be a patient encounter at the CE within
          <span class="protocol-value">{{ patientOwnershipMonths }} Months</span>
          {{ patientOwnershipBeforeText }} prescription {{ patientOwnershipDateText }} date
        </div>
      </div>
    </div>

    <div class="px-3 py-2 border-top border-bottom protocol-subsection-header text-uppercase">
      Claim Eligibility
    </div>

    <div class="protocol-rows">
      <div class="row g-0 px-3 py-3 protocol-row">
        <div class="col-5 ">What documentation is required to capture a 340B referral claim?</div>
        <div class="col-7">
          @if (patientCareOwnershipOnly) {
            <div class="protocol-option">
              <mat-icon>check_box</mat-icon>
              <span>Patient Care Ownership Only</span>
            </div>
          }
          <div class="protocol-option" [class.text-muted]="documentationRequired !== 'referral-only'">
            <mat-icon>{{ documentationRequired === 'referral-only' ? 'check_box' : 'check_box_outline_blank' }}</mat-icon>
            <span>Referral Only</span>
          </div>
          <div class="protocol-option" [class.text-muted]="documentationRequired !== 'consult-only'">
            <mat-icon>{{ documentationRequired === 'consult-only' ? 'check_box' : 'check_box_outline_blank' }}</mat-icon>
            <span>Consult Note Only</span>
          </div>
          <div class="protocol-option" [class.text-muted]="documentationRequired !== 'both'">
            <mat-icon>{{ documentationRequired === 'both' ? 'check_box' : 'check_box_outline_blank' }}</mat-icon>
            <span>Referral and Consult Note</span>
          </div>
        </div>
      </div>

      <div class="row g-0 px-3 py-3 protocol-row">
        <div class="col-5 ">Approve claims based on</div>
        <div class="col-7">
          <div class="protocol-option" [class.text-muted]="!configEnabled(Keys.drugNotRequiredOnConsultNote)">
            <mat-icon>{{ configEnabled(Keys.drugNotRequiredOnConsultNote) ? 'check_box' : 'check_box_outline_blank' }}</mat-icon>
            <span>Patient and Provider</span>
          </div>
          <div class="protocol-option" [class.text-muted]="configEnabled(Keys.drugNotRequiredOnConsultNote)">
            <mat-icon>{{ !configEnabled(Keys.drugNotRequiredOnConsultNote) ? 'check_box' : 'check_box_outline_blank' }}</mat-icon>
            <span>Patient, Provider, and Drug Group</span>
          </div>
        </div>
      </div>

      <div class="row g-0 px-3 py-3 protocol-row">
        <div class="col-5 ">Allow outreach attempts to replace consult note evidence?</div>
        <div class="col-7 d-flex gap-4">
          <div class="protocol-option" [class.text-muted]="!configEnabled(Keys.enableOutreachAttemptEvidence)">
            <mat-icon>{{ configEnabled(Keys.enableOutreachAttemptEvidence) ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
            <span>Yes</span>
          </div>
          <div class="protocol-option" [class.text-muted]="configEnabled(Keys.enableOutreachAttemptEvidence)">
            <mat-icon>{{ !configEnabled(Keys.enableOutreachAttemptEvidence) ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
            <span>No</span>
          </div>
        </div>
      </div>

      <div class="row g-0 px-3 py-3 protocol-row">
        <div class="col-5 ">Specialist Encounter Window</div>
        <div class="col-7">
          Specialist encounter must be within
          <span class="protocol-value">{{ specialistEncounterMonths }} Months</span>
          of prescription written date.
        </div>
      </div>

      @if (configEnabled(Keys.ehrAccess)) {
        <div class="row g-0 px-3 py-3 protocol-row">
          <div class="col-5 ">EHR access granted to NuvemRx for claim eligibility review?</div>
          <div class="col-7 d-flex gap-4">
            <div class="protocol-option">
              <mat-icon>radio_button_checked</mat-icon>
              <span>Yes</span>
            </div>
            <div class="protocol-option text-muted">
              <mat-icon>radio_button_unchecked</mat-icon>
              <span>No</span>
            </div>
          </div>
        </div>
      }
    </div>
  </div>

  <div class="mt-4 border rounded overflow-hidden">
    <div class="bg-dark text-white p-2 px-3 text-uppercase protocol-section-header">
      Other Program Configurations
    </div>

    <div class="protocol-rows">
      <div class="protocol-row">
        <div class="px-3 pt-3 pb-1 ">Direct Secure Messaging</div>
        <div class="row g-0 px-3 pb-3">
          <div class="col-5 text-muted">Use Direct Secure Messaging to automatically return consult notes collected?</div>
          <div class="col-7 d-flex gap-4">
            <div class="protocol-option" [class.text-muted]="!client.directEnabled">
              <mat-icon>{{ client.directEnabled ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
              <span>Yes</span>
            </div>
            <div class="protocol-option" [class.text-muted]="client.directEnabled">
              <mat-icon>{{ !client.directEnabled ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
              <span>No</span>
            </div>
          </div>
        </div>
      </div>

      <div class="row g-0 px-3 py-3 protocol-row">
        <div class="col-5 ">Client Direct Secure Messaging Receiving Address</div>
        <div class="col-7">
          <span class="protocol-value protocol-value-block">{{ client.directReceivingAddress || '—' }}</span>
        </div>
      </div>

      <div class="row g-0 px-3 py-3 protocol-row">
        <div class="col-5 ">Client File Feed Error Notifications</div>
        <div class="col-7">
          <span class="protocol-value protocol-value-block">{{ client.clientFileMonitoringEmailRecipients?.join(', ') || '—' }}</span>
        </div>
      </div>

      @if (configEnabled(Keys.referralDateMustBeBeforeRxWrittenDate)) {
        <div class="row g-0 px-3 py-3 protocol-row">
          <div class="col-5 ">Referral must be before prescription written date?</div>
          <div class="col-7 d-flex gap-4">
            <div class="protocol-option">
              <mat-icon>radio_button_checked</mat-icon>
              <span>Yes</span>
            </div>
            <div class="protocol-option text-muted">
              <mat-icon>radio_button_unchecked</mat-icon>
              <span>No</span>
            </div>
          </div>
        </div>
      }

      @if (configEnabled(Keys.moveNeedsEvidenceCapturesToCeReviewEmrConsultNoteReviewRequired)) {
        <div class="row g-0 px-3 py-3 protocol-row">
          <div class="col-5 ">Client checks for existing consult note evidence prior to NuvemRx outreach?</div>
          <div class="col-7 d-flex gap-4">
            <div class="protocol-option">
              <mat-icon>radio_button_checked</mat-icon>
              <span>Yes</span>
            </div>
            <div class="protocol-option text-muted">
              <mat-icon>radio_button_unchecked</mat-icon>
              <span>No</span>
            </div>
          </div>
        </div>
      }

      @if (configEnabled(Keys.disableOutboundFaxes)) {
        <div class="row g-0 px-3 py-3 protocol-row">
          <div class="col-5 ">Block outbound faxing for specialist encounter notes?</div>
          <div class="col-7 d-flex gap-4">
            <div class="protocol-option">
              <mat-icon>radio_button_checked</mat-icon>
              <span>Yes</span>
            </div>
            <div class="protocol-option text-muted">
              <mat-icon>radio_button_unchecked</mat-icon>
              <span>No</span>
            </div>
          </div>
        </div>
      }

      @if (configEnabled(Keys.bypassPrimaryCareRejectionTransitioners)) {
        <div class="row g-0 px-3 py-3 protocol-row">
          <div class="col-5 ">Primary care eligible for referral capture?</div>
          <div class="col-7 d-flex gap-4">
            <div class="protocol-option">
              <mat-icon>radio_button_checked</mat-icon>
              <span>Yes</span>
            </div>
            <div class="protocol-option text-muted">
              <mat-icon>radio_button_unchecked</mat-icon>
              <span>No</span>
            </div>
          </div>
        </div>
      }
    </div>
  </div>

} @else if (loading) {
  <mat-spinner diameter="30" class="protocol-spinner" />
} @else {
  <p class="text-danger mt-3">Unable to load client data.</p>
}
